import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MultiSelectPopover } from '@/shared/components/MultiSelectPopover';
import { User, History, Building2, RefreshCw, Trash2, ShieldCheck } from 'lucide-react';
import { UserStatusBadge } from '../UserStatusBadge';
import { SessionLogTab } from '../SessionLogTab';
import { SecurityLogTab } from '../SecurityLogTab';
import { MultiSelectCheckbox } from '../MultiSelectCheckbox';
import { TagsSelector } from '../TagsSelector';
import { DeleteUserDialog } from './DeleteUserDialog';
import {
  useNewUserOptions,
  useUpdateOrganisationProfile,
  useRemoveUserFromOrganisation,
  useDeleteUser,
} from '../../hooks';
import { ApiError } from '@/lib/api';
import type {
  User as UserType,
  UpdateUserInput,
  PopulatedOrganisation,
  AdminLevel,
} from '../../types';

interface UserFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserType | null;
  onSubmit: (data: UpdateUserInput) => Promise<void>;
  isSubmitting?: boolean;
  canDeleteEntirely?: boolean;
  isFullAdmin?: boolean;
  /**
   * Whether the logged-in driver may change this user's email. Mirrors the
   * web_api_server field tier (full_admin / director, or a contact-manager
   * sales_manager) — defaults to `isFullAdmin` when not supplied.
   */
  canEditEmail?: boolean;
  accountId?: string;
  initialOrganisationId?: string;
  lockOrganisation?: boolean;
}

// Client-side UX gate only — the backend (PUT /v4/users/:id/profile)
// normalizes and remains the authority. No spaces, one @, a dot in the domain.
const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ADMIN_LEVELS: { value: AdminLevel; label: string }[] = [
  { value: 'standard user', label: 'Standard User' },
  { value: 'group admin', label: 'Group Admin' },
  { value: 'full admin', label: 'Full Admin' },
  { value: 'auditor', label: 'Auditor' },
];

function getOrganisationId(
  organisation: NonNullable<UserType['organisations']>[number]['organisation'],
) {
  if (typeof organisation === 'string') return organisation;
  return organisation?._id || '';
}

export function UserFormModal({
  open,
  onOpenChange,
  user,
  onSubmit,
  isSubmitting,
  canDeleteEntirely = false,
  isFullAdmin = false,
  canEditEmail,
  accountId,
  initialOrganisationId,
  lockOrganisation = false,
}: UserFormModalProps) {
  const { t } = useTranslation('users');

  // Email editability mirrors the backend field tier: full admins (and
  // directors) always; contact managers (sales_manager) via the caller's
  // prop; everyone else read-only.
  const emailEditable = canEditEmail ?? isFullAdmin;

  // Active tab
  const [activeTab, setActiveTab] = useState('details');

  // Form state for user details
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [company, setCompany] = useState('');
  const [position, setPosition] = useState('');
  const [language, setLanguage] = useState<'en' | 'fr'>('en');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Organisation profile state
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [adminLevel, setAdminLevel] = useState<AdminLevel>('standard user');
  const [selectedUsergroups, setSelectedUsergroups] = useState<string[]>([]);
  const [selectedSensorgroups, setSelectedSensorgroups] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Queries and mutations
  const newUserOptionsQuery = useNewUserOptions(
    selectedOrgId || undefined,
    lockOrganisation ? accountId : undefined,
  );
  const updateOrgProfileMutation = useUpdateOrganisationProfile();
  const removeFromOrgMutation = useRemoveUserFromOrganisation();
  const deleteUserMutation = useDeleteUser();

  useEffect(() => {
    if (!open) setActiveTab('details');
  }, [open]);

  useEffect(() => {
    if (user?._id) setActiveTab('details');
  }, [user?._id]);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
      setPhone(user.phone || '');
      setMobile(user.mobile || '');
      setCompany(user.company || '');
      setPosition(user.position || '');
      setLanguage(user.language || 'en');
      setEmail(user.email || '');
      setEmailError(null);

      const preferredOrgProfile =
        user.organisations?.find(
          (profile) => getOrganisationId(profile.organisation) === initialOrganisationId
        ) || user.organisations?.[0];

      if (preferredOrgProfile) {
        const orgId = getOrganisationId(preferredOrgProfile.organisation);
        setSelectedOrgId(orgId);
        setAdminLevel(preferredOrgProfile.admin_level || 'standard user');
        setSelectedUsergroups(
          (preferredOrgProfile.usergroups || [])
            .map((ug) => (typeof ug === 'string' ? ug : ug._id))
            .filter(Boolean)
        );
        setSelectedSensorgroups(
          (preferredOrgProfile.sensorgroups || [])
            .map((sg) => (typeof sg === 'string' ? sg : sg._id))
            .filter(Boolean)
        );
        setSelectedTags(preferredOrgProfile.tags || []);
      } else {
        setSelectedOrgId('');
        setSelectedUsergroups([]);
        setSelectedSensorgroups([]);
        setSelectedTags([]);
      }
    }
  }, [user, open, initialOrganisationId]);

  // Update org profile state when selected org changes
  useEffect(() => {
    if (!user || !selectedOrgId) return;

    const orgProfile = user.organisations?.find((op) => {
      const orgId =
        typeof op.organisation === 'string'
          ? op.organisation
          : (op.organisation as PopulatedOrganisation)?._id;
      return orgId === selectedOrgId;
    });

    if (orgProfile) {
      setAdminLevel(orgProfile.admin_level || 'standard user');
      setSelectedUsergroups(
        (orgProfile.usergroups || [])
          .map((ug) => (typeof ug === 'string' ? ug : ug._id))
          .filter(Boolean)
      );
      setSelectedSensorgroups(
        (orgProfile.sensorgroups || [])
          .map((sg) => (typeof sg === 'string' ? sg : sg._id))
          .filter(Boolean)
      );
      setSelectedTags(orgProfile.tags || []);
    }
  }, [selectedOrgId, user]);

  if (!user) return null;

  const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ') || '(No name)';

  // Get organisation list for dropdown
  const orgOptions = (user.organisations || [])
    .map((op) => {
      if (!op.organisation) return null;
      if (typeof op.organisation === 'string') {
        return { _id: op.organisation, name: op.organisation };
      }
      const org = op.organisation as PopulatedOrganisation;
      return org ? { _id: org._id, name: org.name } : null;
    })
    .filter((o): o is { _id: string; name: string } => o !== null);

  const selectedOrgName = orgOptions.find((o) => o._id === selectedOrgId)?.name;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // A changed email is a new login identity — validate the shape up front
    // and only send it when it actually changed (an unchanged address must
    // not ride along and risk a self-collision 409).
    const originalEmail = user.email || '';
    const trimmedEmail = email.trim();
    const emailChanged = emailEditable && trimmedEmail !== originalEmail;
    if (emailChanged && !EMAIL_FORMAT_RE.test(trimmedEmail)) {
      setEmailError(t('modal.emailInvalid', 'Enter a valid email address'));
      return;
    }

    try {
      await onSubmit({
        ...(emailChanged ? { email: trimmedEmail } : {}),
        firstname,
        lastname,
        phone: phone || undefined,
        mobile: mobile || undefined,
        company: company || undefined,
        position: position || undefined,
        language,
      });
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        (error.data as { error?: string } | undefined)?.error === 'email_or_username_in_use'
      ) {
        setEmailError(t('modal.emailInUse', 'This email is already in use'));
      }
      // Any other failure is toasted by the useUpdateUser hook — keep the
      // modal open so the user's edits are not lost.
      return;
    }
    setEmailError(null);
    onOpenChange(false);
  };

  const handleSaveOrgProfile = async () => {
    if (!user._id || !selectedOrgId) return;

    await updateOrgProfileMutation.mutateAsync({
      userId: user._id,
      organisationId: selectedOrgId,
      data: {
        admin: adminLevel,
        usergroups: selectedUsergroups,
        sensorgroups: selectedSensorgroups,
        tags: selectedTags,
      },
    });
  };

  const handleRemoveFromOrg = async () => {
    if (!user._id || !selectedOrgId) return;

    try {
      await removeFromOrgMutation.mutateAsync({
        userId: user._id,
        organisationId: selectedOrgId,
      });
      setDeleteDialogOpen(false);
      onOpenChange(false);
    } catch {
      // Error toast shown by hook, keep dialog open for retry
    }
  };

  const handleDeleteEntirely = async () => {
    // PG-only: deletes always identify users by PG UUID.
    const pgId = user.id ?? user.pg_id;
    if (!pgId) return;

    try {
      await deleteUserMutation.mutateAsync(pgId);
      setDeleteDialogOpen(false);
      onOpenChange(false);
    } catch {
      // Error toast shown by hook, keep dialog open for retry
    }
  };

  // Check if there are changes in user details
  const hasDetailsChanges =
    firstname !== (user.firstname || '') ||
    lastname !== (user.lastname || '') ||
    phone !== (user.phone || '') ||
    mobile !== (user.mobile || '') ||
    company !== (user.company || '') ||
    position !== (user.position || '') ||
    language !== (user.language || 'en') ||
    (emailEditable && email.trim() !== (user.email || ''));

  // Check if there are changes in org profile
  const hasOrgProfileChanges = (() => {
    if (!selectedOrgId) return false;
    const orgProfile = user.organisations?.find((op) => {
      const orgId =
        typeof op.organisation === 'string'
          ? op.organisation
          : (op.organisation as PopulatedOrganisation)?._id;
      return orgId === selectedOrgId;
    });
    if (!orgProfile) return false;

    const originalUsergroups = (orgProfile.usergroups || [])
      .map((ug) => (typeof ug === 'string' ? ug : ug._id))
      .filter(Boolean)
      .sort();
    const originalSensorgroups = (orgProfile.sensorgroups || [])
      .map((sg) => (typeof sg === 'string' ? sg : sg._id))
      .filter(Boolean)
      .sort();
    const originalTags = [...(orgProfile.tags || [])].sort();

    return (
      adminLevel !== (orgProfile.admin_level || 'standard user') ||
      JSON.stringify([...selectedUsergroups].sort()) !== JSON.stringify(originalUsergroups) ||
      JSON.stringify([...selectedSensorgroups].sort()) !== JSON.stringify(originalSensorgroups) ||
      JSON.stringify([...selectedTags].sort()) !== JSON.stringify(originalTags)
    );
  })();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update information for {fullName} ({user.email})
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="details" className="flex items-center gap-1">
                <User className="h-4 w-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="organisation" className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                Account
              </TabsTrigger>
              <TabsTrigger value="sessions" className="flex items-center gap-1">
                <History className="h-4 w-4" />
                Sessions
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-1">
                <ShieldCheck className="h-4 w-4" />
                Security Log
              </TabsTrigger>
            </TabsList>

            {/* User Details Tab */}
            <TabsContent value="details" className="mt-4">
              {/* noValidate: the browser's native email bubble is
                  English-only and untranslatable — validate ourselves so
                  the error shows through i18n (modal.emailInvalid). */}
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-6">
                  {/* User Info Summary */}
                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="flex items-center justify-between gap-4">
                      {emailEditable ? (
                        <Label
                          htmlFor="email"
                          className="text-sm text-muted-foreground shrink-0"
                        >
                          {t('modal.email', 'Email')}
                        </Label>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t('modal.email', 'Email')}
                        </span>
                      )}
                      {emailEditable ? (
                        <div className="flex-1 max-w-sm space-y-1">
                          <Input
                            id="email"
                            type="email"
                            autoComplete="off"
                            className="font-mono text-sm h-8 bg-background"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              if (emailError) setEmailError(null);
                            }}
                          />
                          {emailError && (
                            <p role="alert" className="text-xs text-destructive">
                              {emailError}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="font-mono text-sm">{user.email}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <UserStatusBadge enabled={user.enabled} />
                    </div>
                    {user.login_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Last Login</span>
                        <span className="text-sm">{new Date(user.login_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstname">First Name</Label>
                      <Input
                        id="firstname"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastname">Last Name</Label>
                      <Input
                        id="lastname"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mobile">Mobile</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="Mobile number"
                      />
                    </div>
                  </div>

                  {/* Work Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Company name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="position">Position</Label>
                      <Input
                        id="position"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        placeholder="Job title"
                      />
                    </div>
                  </div>

                  {/* Language */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="language">Language</Label>
                      <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'fr')}>
                        <SelectTrigger id="language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="fr">French</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <DialogFooter className="mt-6">
                  {isFullAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      className="mr-auto border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete User
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting || !hasDetailsChanges}>
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>

            {/* Organisation Profile Tab */}
            <TabsContent value="organisation" className="mt-4">
              <div className="space-y-6">
                {orgOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    User is not a member of any accounts
                  </p>
                ) : (
                  <>
                    {/* Organisation Selector */}
                    <div className="space-y-2">
                      <Label>Account</Label>
                      <Select
                        value={selectedOrgId}
                        onValueChange={setSelectedOrgId}
                        disabled={lockOrganisation}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                        <SelectContent>
                          {orgOptions.map((org) => (
                            <SelectItem key={org._id} value={org._id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedOrgId && (
                      <>
                        {/* Admin Level */}
                        <div className="space-y-2">
                          <Label>Admin Level</Label>
                          <Select
                            value={adminLevel}
                            onValueChange={(v) => setAdminLevel(v as AdminLevel)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ADMIN_LEVELS.map((level) => (
                                <SelectItem key={level.value} value={level.value}>
                                  {level.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Tags Selector - Only for full admins */}
                        {isFullAdmin && (
                          <TagsSelector
                            selectedTags={selectedTags}
                            onChange={setSelectedTags}
                            organisationId={selectedOrgId}
                          />
                        )}

                        {/* Error state for loading options */}
                        {newUserOptionsQuery.isError && (
                          <div className="text-center py-4 border rounded-lg bg-muted/50">
                            <p className="text-sm text-red-500 mb-2">
                              Failed to load user groups and sensor groups
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => newUserOptionsQuery.refetch()}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Retry
                            </Button>
                          </div>
                        )}

                        {/* User Groups */}
                        {!newUserOptionsQuery.isError && (
                          <MultiSelectCheckbox
                            label="User Groups"
                            options={newUserOptionsQuery.data?.usergroups || []}
                            selected={selectedUsergroups}
                            onChange={setSelectedUsergroups}
                            isLoading={newUserOptionsQuery.isLoading}
                            emptyMessage="No user groups available"
                          />
                        )}

                        {/* Sensor Groups */}
                        {!newUserOptionsQuery.isError && (
                          <div className="space-y-2">
                            <Label>Sensor Groups</Label>
                            {newUserOptionsQuery.isLoading ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                <RefreshCw className="h-4 w-4 animate-spin" />
                                Loading...
                              </div>
                            ) : (
                              <MultiSelectPopover
                                value={selectedSensorgroups}
                                options={(newUserOptionsQuery.data?.sensorgroups || []).map((group) => ({
                                  id: group._id,
                                  label: group.name,
                                }))}
                                onChange={setSelectedSensorgroups}
                                placeholder="Select sensor groups..."
                                searchPlaceholder="Search sensor groups..."
                                emptyMessage="No sensor groups available"
                                noMatchesMessage="No matching groups"
                                showSelectAll
                                selectAllLabel="Select all sensor groups"
                              />
                            )}
                            {!newUserOptionsQuery.isLoading && selectedSensorgroups.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {selectedSensorgroups.length} selected
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              {selectedOrgId && orgOptions.length > 0 && (
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveOrgProfile}
                    disabled={updateOrgProfileMutation.isPending || !hasOrgProfileChanges}
                  >
                    {updateOrgProfileMutation.isPending ? 'Saving...' : 'Save Account Profile'}
                  </Button>
                </DialogFooter>
              )}
            </TabsContent>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="mt-4">
              <SessionLogTab userId={user._id} />
            </TabsContent>

            <TabsContent value="security" className="mt-4">
              <SecurityLogTab user={user} isFullAdmin={isFullAdmin} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <DeleteUserDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        user={user}
        selectedOrgId={selectedOrgId}
        selectedOrgName={selectedOrgName}
        onRemoveFromOrg={handleRemoveFromOrg}
        onDeleteEntirely={handleDeleteEntirely}
        isRemovingFromOrg={removeFromOrgMutation.isPending}
        isDeletingEntirely={deleteUserMutation.isPending}
        canDeleteEntirely={canDeleteEntirely}
      />
    </>
  );
}
